import { supabase } from '../supabaseClient';

// __RETURN_HOST__ (2026-09-22) Billing answers on more than one name --
// billing.hmgc.io and hub.hmgc.io point at the same service -- so "send the
// user back to billing" has to mean "back to the name they came from". A login
// started on hub that lands on billing works, but it silently swaps the name
// the user is on.
//
// THIS IS AN ALLOW-LIST ON PURPOSE. The handoff below puts the ACCESS and
// REFRESH tokens in the query string. Taking the return address from the URL
// as given would be a textbook open redirect, and here an open redirect is a
// token leak: a crafted ?host=evil.example link would have this app hand a
// live session to whoever wrote it. So the host is only ever one of these
// exact names -- no suffix match, no wildcard, nothing built from input.
// Adding a name means adding it here AND in billing's _BILLING_HOSTS.
const BILLING_HOSTS = ['billing.hmgc.io', 'hub.hmgc.io'];
// __BILLING_TO_HUB__ (2026-09-23) billing.hmgc.io now forwards browsers to hub, so
// defaulting to hub saves a hop -- and the Fleet Manager -> Billing button, which
// never carries ?host=, lands on the current name.
const DEFAULT_BILLING_HOST = 'hub.hmgc.io';
const RETURN_HOST_KEY = 'billingReturnHost';

// Exact membership or nothing. Returns the canonical host, or null.
export function allowedBillingHost(h) {
  const v = String(h || '').trim().toLowerCase();
  return BILLING_HOSTS.indexOf(v) !== -1 ? v : null;
}

// Remember which billing name sent the user here, BEFORE they sign in. A
// Google sign-in navigates away and returns without the query string, so the
// URL alone cannot carry it through the login step.
//
// It only ever SETS. A stale value is harmless here in a way the old sticky
// postLoginRedirect was not: this chooses WHICH billing name, never WHETHER to
// redirect -- postLoginRedirect still decides that -- and every value it can
// hold is on the allow-list.
export function rememberBillingHost() {
  try {
    const h = allowedBillingHost(new URLSearchParams(window.location.search).get('host'));
    if (h) sessionStorage.setItem(RETURN_HOST_KEY, h);
  } catch (e) { /* storage blocked: the default host is used */ }
}

// Where to send the user: the URL's host if allowed, else the one remembered
// before sign-in, else the default. Single-use -- consumed on the way out.
function billingUrl() {
  let h = null;
  try { h = allowedBillingHost(new URLSearchParams(window.location.search).get('host')); } catch (e) { h = null; }
  if (!h) {
    try { h = allowedBillingHost(sessionStorage.getItem(RETURN_HOST_KEY)); } catch (e) { h = null; }
  }
  try { sessionStorage.removeItem(RETURN_HOST_KEY); } catch (e) { /* ignore */ }
  return 'https://' + (h || DEFAULT_BILLING_HOST);
}

// Hand off the current Supabase session + role to the billing app.
// Uses window.location.replace so the intermediate dashboard state
// doesn't end up in browser history -- matches App.js's bounce-back
// behavior and avoids the boot-loop risk that 9b46e28 fixed.
export async function redirectToBilling(role) {
  const BILLING_URL = billingUrl();
  const { data: { session } } = await supabase.auth.getSession();
  const accessToken = session?.access_token || '';
  const refreshToken = session?.refresh_token || '';
  // __RETURN_TO_SPOT__ (2026-09-13) The #__last__ sentinel asks billing to put the
  // user back on the tab they were on before they came here, instead of dropping
  // them on the default landing. Billing owns that memory -- nothing about where
  // someone was in billing needs to cross to this app. A fragment is never sent to
  // a server, and the browser carries it across billing's auth redirect, so it
  // survives the token handoff without being logged anywhere.
  const RETURN = '#__last__';
  if (accessToken) {
    window.location.replace(
      BILLING_URL + '?token=' + accessToken +
      '&refresh_token=' + refreshToken +
      '&role=' + role + RETURN
    );
  } else {
    window.location.replace(BILLING_URL + '/' + RETURN);
  }
}
